import { createStore } from 'zustand/vanilla'
import { laneHasFlow } from '@/config/laneConfig'

/**
 * The loaded network and the optimisation configuration. Ported verbatim from
 * the Pinia options store (Phase 1B). Row types stay loose on purpose — they
 * are whatever the API returns and were never typed; they tighten in 1E.
 */
export type Facility = Record<string, unknown> & {
  name?: string
  facility_name?: string
  type?: string
  facility_type?: string
  location_type?: string
  capacity?: number
  max_capacity?: number
  units_processed?: number
  total_processed?: number
  is_active?: boolean
  is_open?: boolean
}
export type Lane = Record<string, unknown> & {
  origin?: string
  origin_name?: string
  destination?: string
  destination_name?: string
  flow?: number
  cost_per_unit?: number
}
export type BaselineCosts = {
  total_cost: number
  fixed_cost: number
  variable_cost: number
  shipping_cost: number
  demand_fulfilled: number
  fulfillment_percentage: number
  product_count: number
}
export type PurchaseMethodStats = { methods: unknown[]; totalFlow: number; skuCount: number; shippingCost: number } & Record<string, unknown>
export type OptimizationConfig = {
  multipliers: Record<string, number>
  objectiveFunction: Record<string, unknown>
  decisionSpace: Record<string, string>
  fixedN: unknown[]
}
/** The map's filter set, as useLaneManagement.js defined it. */
export type MapFilters = {
  showLegend: boolean
  showServiceLevelHeatMap: boolean
  activeLanes: boolean
  activeNodes: boolean
  showNames: boolean
  locationType: string
  networkLocations: string[]
  networkSegments: string[]
  capacity: { constrained: boolean; atRisk: boolean; available: boolean }
  capacityCounts: { constrained: number; atRisk: number; available: number }
  capacityMetric: string
}

export const DEFAULT_MAP_FILTERS: MapFilters = {
  showLegend: false,
  showServiceLevelHeatMap: false,
  activeLanes: false,
  activeNodes: false,
  showNames: false,
  locationType: 'All Types',
  networkLocations: [],
  networkSegments: [],
  capacity: { constrained: false, atRisk: false, available: false },
  capacityCounts: { constrained: 0, atRisk: 0, available: 0 },
  capacityMetric: 'storage',
}

export type NetworkDataState = {
  /**
   * What the map is filtered to. It was a ref inside useLaneManagement.js until
   * Phase 1D; the quick filter bar is chrome, so a command — and therefore
   * React — has to be able to read and change it.
   */
  filters: MapFilters
  /**
   * The network object the map draws, exactly as it was loaded.
   *
   * The four arrays below are its rows, published for everything else on
   * screen. This is the whole payload, kept because the renderer takes it
   * verbatim and it can carry keys the rows do not.
   *
   * It is here, and not a `ref` in MapView plus a `let` in the filters
   * command, because there were THREE copies of the network and they could
   * disagree: after an optimization, `runOptimization` published fresh rows
   * (so the stat cards followed) while MapView's own ref kept the old ones,
   * and a settings-triggered redraw drew the stale network. One owner makes
   * that impossible rather than unlikely.
   */
  renderableNetwork: unknown
  facilities: Facility[]
  lanes: Lane[]
  customers: Record<string, unknown>[]
  transport_modes: Record<string, unknown>[]
  currentScenarioId: number | null
  currentScenarioName: string | null
  isLoading: boolean
  baselineCosts: BaselineCosts
  hasOptimizedBaseline: boolean
  purchaseMethodStats: PurchaseMethodStats
  optimizationConfig: OptimizationConfig
}

const emptyBaselineCosts = (): BaselineCosts => ({
  total_cost: 0, fixed_cost: 0, variable_cost: 0, shipping_cost: 0, demand_fulfilled: 0, fulfillment_percentage: 0, product_count: 0,
})
const emptyPurchaseMethodStats = (): PurchaseMethodStats => ({ methods: [], totalFlow: 0, skuCount: 0, shippingCost: 0 })

function defaults(): NetworkDataState {
  return {
    renderableNetwork: null,
    facilities: [],
    lanes: [],
    customers: [],
    transport_modes: [],
    currentScenarioId: null,
    currentScenarioName: null,
    isLoading: false,
    baselineCosts: emptyBaselineCosts(),
    hasOptimizedBaseline: false,
    purchaseMethodStats: emptyPurchaseMethodStats(),
    filters: { ...DEFAULT_MAP_FILTERS, capacity: { ...DEFAULT_MAP_FILTERS.capacity }, capacityCounts: { ...DEFAULT_MAP_FILTERS.capacityCounts } },
    optimizationConfig: {
      multipliers: {
        fixed_cost_multiplier: 1.0, variable_cost_multiplier: 1.0, shipping_cost_multiplier: 1.0,
        demand_multiplier: 1.0, max_capacity_multiplier: 1.0, min_capacity_multiplier: 1.0,
      },
      objectiveFunction: { primary: 'minimize_total_cost', fallbackConditionType: 'demand', fallbackConditionValue: 100, fallback: 'minimize_distance' },
      decisionSpace: { dc: 'Brownfield', warehouse: 'Brownfield', port: 'Brownfield', plant: 'Brownfield' },
      fixedN: [],
    },
  }
}

export const networkDataStore = createStore<NetworkDataState>(() => defaults())

const typeOf = (f: Facility) => (f.type || f.location_type || '').toLowerCase()

export const networkDataSelectors = {
  getFacilityByName: (s: NetworkDataState) => (name: string) => s.facilities.find((f) => f.name === name || f.facility_name === name),
  getOutboundLanes: (s: NetworkDataState) => (facilityName: string) => s.lanes.filter((l) => l.origin === facilityName || l.origin_name === facilityName),
  getInboundLanes: (s: NetworkDataState) => (facilityName: string) => s.lanes.filter((l) => l.destination === facilityName || l.destination_name === facilityName),
  getFacilitiesByType: (s: NetworkDataState) => (type: string) => s.facilities.filter((f) => f.type === type || f.facility_type === type || f.location_type === type),
  getLanesWithFlow: (s: NetworkDataState) => s.lanes.filter((l) => laneHasFlow(l)),
  totalFacilities: (s: NetworkDataState) => s.facilities.length,
  totalLanes: (s: NetworkDataState) => s.lanes.length,
  facilityStats: (s: NetworkDataState) => {
    const facilities = s.facilities
    const facilitiesWithCapacity = facilities.filter((f) => (f.capacity || f.max_capacity || 0) > 0)
    const totalCapacity = facilities.reduce((sum, f) => sum + (f.capacity || f.max_capacity || 0), 0)
    let avgUtilization = 0
    if (facilitiesWithCapacity.length > 0) {
      const totalUtilization = facilitiesWithCapacity.reduce((sum, f) => {
        const capacity = f.capacity || f.max_capacity || 1
        const processed = f.units_processed || f.total_processed || 0
        return sum + processed / capacity
      }, 0)
      avgUtilization = (totalUtilization / facilitiesWithCapacity.length) * 100
    }
    return {
      total: facilities.length,
      active: facilities.filter((f) => f.is_active || f.is_open).length,
      inactive: facilities.filter((f) => !f.is_active && !f.is_open).length,
      byType: {
        plants: facilities.filter((f) => typeOf(f).includes('plant')).length,
        dcs: facilities.filter((f) => typeOf(f).includes('dc')).length,
        warehouses: facilities.filter((f) => typeOf(f).includes('warehouse')).length,
        customers: facilities.filter((f) => typeOf(f).includes('customer')).length,
      },
      totalCapacity,
      avgUtilization,
    }
  },
  laneStats: (s: NetworkDataState) => {
    const lanes = s.lanes
    return {
      total: lanes.length,
      active: lanes.filter((l) => laneHasFlow(l)).length,
      inactive: lanes.filter((l) => !laneHasFlow(l)).length,
      totalFlow: lanes.reduce((sum, l) => sum + (l.flow || 0), 0),
      totalShippingCost: lanes.reduce((sum, l) => sum + (l.flow || 0) * (l.cost_per_unit || 0), 0),
    }
  },
}

export const networkDataActions = {
  /**
   * The network on screen, replaced as a whole; null clears it. Written only by
   * `setRenderableNetwork` (core/commands/filters.ts). Until Phase 1G nothing
   * called this at all — on main too — so these four collections were always
   * empty and every `transport_modes` reader found nothing. It also merged,
   * which would have kept a previous scenario's customers under a network that
   * had none.
   */
  setNetworkData(data: Partial<Pick<NetworkDataState, 'facilities' | 'lanes' | 'customers' | 'transport_modes'>> | null) {
    const state = networkDataStore.getState()
    // An absent collection becomes empty — but the SAME empty array it already
    // held, when it was already empty. A fresh `[]` per publish changes
    // identity, and identity is what every consumer's computed re-runs on: a
    // payload with no `customers` (most of them) re-ran the card component's
    // customer maths on every network load for nothing.
    const keep = <K extends 'facilities' | 'lanes' | 'customers' | 'transport_modes'>(key: K) => {
      const next = data?.[key]
      if (next) return next as NetworkDataState[K]
      const current = state[key]
      return (current.length === 0 ? current : []) as NetworkDataState[K]
    }
    networkDataStore.setState({
      // The payload itself, so the renderer and every row reader are looking
      // at one network rather than two that drifted apart.
      renderableNetwork: data ?? null,
      facilities: keep('facilities'),
      lanes: keep('lanes'),
      customers: keep('customers'),
      transport_modes: keep('transport_modes'),
    })
  },
  setCurrentScenario(scenarioName: string | null, scenarioId: number | null) {
    networkDataStore.setState({ currentScenarioName: scenarioName, currentScenarioId: scenarioId })
  },
  setBaselineCosts(costs: Partial<BaselineCosts>) {
    networkDataStore.setState({
      baselineCosts: {
        total_cost: costs.total_cost || 0,
        fixed_cost: costs.fixed_cost || 0,
        variable_cost: costs.variable_cost || 0,
        shipping_cost: costs.shipping_cost || 0,
        demand_fulfilled: costs.demand_fulfilled || 0,
        fulfillment_percentage: costs.fulfillment_percentage || 0,
        product_count: costs.product_count || 0,
      },
      hasOptimizedBaseline: (costs.total_cost || 0) > 0,
    })
  },
  setPurchaseMethodStats(stats: PurchaseMethodStats | null | undefined) {
    networkDataStore.setState({ purchaseMethodStats: stats || emptyPurchaseMethodStats() })
  },
  /** As before: the network and its results go, the optimisation configuration and isLoading stay. */
  clearAll() {
    networkDataStore.setState({
      renderableNetwork: null,
      facilities: [], lanes: [], customers: [], transport_modes: [],
      currentScenarioId: null, currentScenarioName: null,
      baselineCosts: emptyBaselineCosts(), hasOptimizedBaseline: false,
      purchaseMethodStats: emptyPurchaseMethodStats(),
    })
  },
  setMultipliers(multipliers: Record<string, number>) {
    networkDataStore.setState((s) => ({ optimizationConfig: { ...s.optimizationConfig, multipliers: { ...multipliers } } }))
  },
  setObjectiveFunction(objectiveData: Record<string, unknown>) {
    networkDataStore.setState((s) => ({ optimizationConfig: { ...s.optimizationConfig, objectiveFunction: { ...objectiveData } } }))
  },
  setDecisionSpace(decisionSpace: Record<string, string>) {
    networkDataStore.setState((s) => ({ optimizationConfig: { ...s.optimizationConfig, decisionSpace: { ...decisionSpace } } }))
  },
  /** Merge a partial filter set, as useLaneManagement.handleFilters did. */
  mergeFilters(partial: Partial<MapFilters> | null | undefined) {
    if (!partial || typeof partial !== 'object') return
    const cur = networkDataStore.getState().filters
    networkDataStore.setState({
      filters: {
        ...cur,
        ...partial,
        capacity: { ...cur.capacity, ...(partial.capacity ?? {}) },
        capacityCounts: { ...cur.capacityCounts, ...(partial.capacityCounts ?? {}) },
      },
    })
  },
  /** Replace the whole set — initialisation and reset. */
  replaceFilters(next: MapFilters) {
    networkDataStore.setState({ filters: next })
  },
  setFixedN(constraints: unknown[]) {
    networkDataStore.setState((s) => ({ optimizationConfig: { ...s.optimizationConfig, fixedN: [...constraints] } }))
  },
}

export function __resetNetworkDataForTests() {
  networkDataStore.setState(defaults(), true)
}
